import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '../lib/auth'
import { apiJson } from '../lib/api'

// Player is intentionally excluded from this picker. v1 has no claim flow —
// players arrive via agent invite, never self-serve. See spec §7.
type SelectableRole = 'agent' | 'coach'

const ALLOWED_INTENDED = new Set<SelectableRole>(['agent', 'coach'])

const roles: { value: SelectableRole; label: string; description: string }[] = [
  {
    value: 'agent',
    label: 'Agent',
    description: 'I represent players. (Free — browse players and manage your roster.)',
  },
  {
    value: 'coach',
    label: 'Coach / Recruiter',
    description: 'I scout and recruit players for a club or program.',
  },
]

function parseIntendedRole(raw: string | null): SelectableRole | null {
  if (!raw) return null
  return ALLOWED_INTENDED.has(raw as SelectableRole) ? (raw as SelectableRole) : null
}

function destinationFor(role: SelectableRole): string {
  return role === 'agent' ? '/onboarding/agent' : '/onboarding/coach'
}

function labelFor(role: SelectableRole): string {
  return role === 'agent' ? 'Agent' : 'Coach / Recruiter'
}

// C4 — friendly copy for the role PUT outcomes. role_already_set is handled
// separately (it's a designed single-shot outcome, not an error).
function friendlyRoleError(key: string): string {
  if (key === 'unauthorized') return 'Your session expired — please sign in again.'
  return "Couldn't save your role. Please try again."
}

type Notice = { tone: 'success' | 'error' | 'info'; text: string }

// C4 — single always-present aria-live region so role-selection outcomes
// (success, the single-shot 409, and errors) are announced to screen
// readers, not just shown silently. Rendering the region unconditionally
// (empty when idle) is what makes the live announcement fire reliably.
function RoleNotice({ notice }: { notice: Notice | null }) {
  const tone =
    notice?.tone === 'error'
      ? 'text-hoop-red'
      : notice?.tone === 'success'
        ? 'text-hoop-green'
        : 'text-hoop-text-2'
  return (
    <p
      role="status"
      aria-live="polite"
      className={`mt-4 text-sm min-h-[1.25rem] ${tone}`}
    >
      {notice?.text ?? ''}
    </p>
  )
}

export default function Onboarding() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: session, isPending } = useSession()
  const intended = parseIntendedRole(searchParams.get('intended_role'))
  const [selected, setSelected] = useState<SelectableRole | null>(intended)
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)
  // Gate for the role check below. Nothing paints until it flips, so a
  // role-set user never sees a frame of the picker.
  const [roleChecked, setRoleChecked] = useState(false)

  useEffect(() => {
    if (!isPending && !session) navigate('/', { replace: true })
  }, [session, isPending, navigate])

  // Role guard. Session alone isn't enough: a user whose role is already set
  // can still land here via bookmark, back button, or the TopNav logo target
  // during the role-resolve window. For them the picker is a dead end —
  // Continue returns 409 role_already_set (single-shot, spec §7). Resolve the
  // stored role first and bounce them to the dashboard. This is a redirect,
  // not a bypass: PUT /api/me/role still rejects a second write.
  useEffect(() => {
    if (isPending || !session) return
    let cancelled = false
    apiJson<{ profile: { role: string | null } }>('/api/me')
      .then(({ profile }) => {
        if (cancelled) return
        if (profile.role) {
          navigate('/dashboard', { replace: true })
          return
        }
        setRoleChecked(true)
      })
      .catch(() => {
        // /api/me failed (offline, expired session). Don't strand the user on
        // a blank page — show the picker. The PUT is still the enforcement
        // point, so a role-set user just gets the 409 branch below.
        if (!cancelled) setRoleChecked(true)
      })
    return () => {
      cancelled = true
    }
  }, [session, isPending, navigate])

  async function submitRole(role: SelectableRole) {
    setSubmitting(true)
    setNotice(null)
    try {
      await apiJson('/api/me/role', {
        method: 'PUT',
        body: JSON.stringify({ role }),
      })
      // C4 — confirm success before the route changes. The brief pause lets
      // the aria-live region announce, then we continue to the setup form
      // (which is itself the visual confirmation). Button stays disabled in
      // the meantime, so there's no double-submit window.
      setNotice({ tone: 'success', text: `Role set to ${labelFor(role)}. Taking you to setup…` })
      window.setTimeout(() => navigate(destinationFor(role), { replace: true }), 700)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown_error'
      // Role is single-shot per spec §7. Previously a 409 silently bounced
      // the user to /dashboard with no feedback (QA TB-6). Now we surface the
      // outcome plainly — the rule is unchanged, just no longer invisible.
      if (msg === 'role_already_set') {
        setNotice({
          tone: 'info',
          text: "Your account role is already set, and it can't be changed — contact support if you need to switch. Taking you to your dashboard…",
        })
        // Same ~700ms pattern as the success path: let the aria-live region
        // announce, then move on. Previously this left the user parked on a
        // notice with nowhere to go. Button stays disabled meanwhile.
        window.setTimeout(() => navigate('/dashboard', { replace: true }), 700)
        return
      }
      setNotice({ tone: 'error', text: friendlyRoleError(msg) })
      setSubmitting(false)
    }
  }

  // Hold everything until the session and the stored role have both resolved.
  // Bare background, no picker — the redirect above fires from inside this
  // window for role-set users, so they never see a role prompt at all.
  if (!roleChecked) {
    return <main className="min-h-screen bg-hoop-bg" />
  }

  // Branch A — intended_role passed from the landing CTA fork (M5 Phase 5).
  // User already picked on the homepage; show a single Continue button.
  if (intended) {
    const roleLabel = labelFor(intended)
    return (
      <main className="min-h-screen flex items-center justify-center p-6 bg-hoop-bg">
        <div className="max-w-xl w-full">
          <h1 className="text-3xl font-bold text-hoop-text mb-2">
            Continue as {roleLabel}
          </h1>
          <p className="text-hoop-text-2 mb-8">
            This choice is permanent — your account role can't be changed later.
          </p>
          <button
            onClick={() => submitRole(intended)}
            disabled={submitting}
            type="button"
            className="bg-hoop-primary text-hoop-primary-fg px-6 py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
          >
            {submitting ? 'Saving…' : 'Continue'}
          </button>
          <RoleNotice notice={notice} />
        </div>
      </main>
    )
  }

  // Branch B — no (valid) intended_role. Show the picker.
  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-hoop-bg">
      <div className="max-w-xl w-full">
        <h1 className="text-3xl font-bold text-hoop-text mb-2">
          Welcome to HoopRoster
        </h1>
        <p className="text-hoop-text-2 mb-8">
          Choose your role to continue.
        </p>
        <div className="space-y-3 mb-8">
          {roles.map(r => (
            <button
              key={r.value}
              type="button"
              onClick={() => setSelected(r.value)}
              className={
                'w-full text-left border rounded-lg p-4 transition bg-hoop-surface ' +
                (selected === r.value
                  ? 'border-hoop-primary shadow-sm'
                  : 'border-hoop-border hover:border-hoop-accent')
              }
            >
              <div className="font-medium text-hoop-text">
                {r.label}
              </div>
              <div className="text-sm text-hoop-text-3 mt-0.5">
                {r.description}
              </div>
            </button>
          ))}
        </div>
        <button
          onClick={() => selected && submitRole(selected)}
          disabled={!selected || submitting}
          type="button"
          className="bg-hoop-primary text-hoop-primary-fg px-6 py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
        >
          {submitting ? 'Saving…' : 'Continue'}
        </button>
        <RoleNotice notice={notice} />
      </div>
    </main>
  )
}
